/**
 * Multi-channel template editor — Email + WhatsApp + SMS in one screen.
 *
 *   ┌──────────────┬──────────────────────────────────────────┐
 *   │  E-Mail │ WhatsApp │ SMS  ← channel tabs                │
 *   ├──────────────┴──────────────────────────────────────────┤
 *   │  Block-Library  │  Live-Preview-Canvas (Email tab)       │
 *   │  (drag-source)  │  drag-and-drop sortable, hover toolbar │
 *   │                 │  with ↑ ↓ × buttons                    │
 *   │                 │                                         │
 *   │  Property panel │  WhatsApp / SMS tabs:                  │
 *   │  for selected   │  rich text-area with format toolbar    │
 *   └─────────────────┴──────────────────────────────────────────┘
 *
 * `dnd-kit` powers the drag interactions. We keep ONE `DndContext`
 * spanning library + canvas so a library item can be dropped directly
 * into the canvas (insert) and canvas blocks can be reordered via the
 * same gesture.
 */
import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Bold,
  GripVertical,
  Image,
  Italic,
  List,
  Loader2,
  Minus,
  Monitor,
  MoonIcon,
  Plus,
  Save,
  Smartphone,
  Sparkles,
  Square,
  SunIcon,
  TextIcon,
  Trash2,
  Type,
  X as XIcon,
} from 'lucide-react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { makeBlock, type MailBlock, type MailTemplate } from '@cp3/shared';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input, Textarea } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Slider } from '../../../components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { useToast } from '../../../components/ui/toast';
import { useMailTemplate, useUpsertMailTemplate } from '../../../hooks/useMailTemplates';
import { compileMailTemplate } from './compileMail';
import { cn } from '../../../lib/utils';

const PREVIEW_TAGS: Record<string, string> = {
  ticketCode: 'T-7AB42',
  fromName: 'Lisa',
  callerName: 'Frau Mustermann',
  appointmentDate: 'Donnerstag, 7. Mai 2026',
  appointmentTime: '14:30',
  eventTypeName: 'Beratung',
  durationMinutes: '30',
  confirmationCode: 'B-9F2K',
  calendarUrl: 'https://example.com/cal',
  ticketCreatedDate: '28. April 2026',
};

type Channel = 'email' | 'whatsapp' | 'sms';

const LIBRARY: ReadonlyArray<{
  type: MailBlock['type'];
  label: string;
  icon: React.ReactNode;
}> = [
  { type: 'heading', label: 'Headline', icon: <Type className="h-4 w-4" /> },
  { type: 'paragraph', label: 'Text', icon: <TextIcon className="h-4 w-4" /> },
  { type: 'button', label: 'Button', icon: <Square className="h-4 w-4" /> },
  { type: 'image', label: 'Bild', icon: <Image className="h-4 w-4" /> },
  { type: 'hero', label: 'Hero', icon: <Sparkles className="h-4 w-4" /> },
  { type: 'divider', label: 'Trenner', icon: <Minus className="h-4 w-4" /> },
  { type: 'spacer', label: 'Spacer', icon: <Square className="h-4 w-4 opacity-40" /> },
  { type: 'columns2', label: '2 Spalten', icon: <Plus className="h-4 w-4" /> },
];

const CANVAS_DROP_ID = '__canvas__';
const LIBRARY_PREFIX = 'lib:';

export function MailTemplateEditor() {
  const { id } = useParams<{ id: string }>();
  const tplQ = useMailTemplate(id);
  const upsert = useUpsertMailTemplate();
  const { toast } = useToast();

  const [draft, setDraft] = React.useState<MailTemplate | null>(null);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [channel, setChannel] = React.useState<Channel>('email');
  const [previewMode, setPreviewMode] = React.useState<'light' | 'dark'>('light');
  const [previewWidth, setPreviewWidth] = React.useState<'desktop' | 'mobile'>('desktop');
  const [confirmDelete, setConfirmDelete] = React.useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Hydrate the editor draft *once* after the first server response
  // arrives. Tracking the hydrated state in a ref instead of including
  // `draft` in the deps prevents the second-render race that
  // previously could clobber unsaved edits when a stale fetch resolved
  // late.
  const hydratedRef = React.useRef(false);
  React.useEffect(() => {
    if (!hydratedRef.current && tplQ.data) {
      hydratedRef.current = true;
      setDraft(tplQ.data);
      setSelectedId(tplQ.data.blocks[0]?.id ?? null);
    }
  }, [tplQ.data]);

  if (tplQ.isLoading || !draft) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade Vorlage …
      </div>
    );
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(tplQ.data);

  function patch<K extends keyof MailTemplate>(key: K, value: MailTemplate[K]) {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  }

  function patchBlock(blockId: string, partial: Partial<MailBlock>) {
    setDraft((d) =>
      d
        ? {
            ...d,
            blocks: d.blocks.map((b) =>
              b.id === blockId ? ({ ...b, ...partial } as MailBlock) : b,
            ),
          }
        : d,
    );
  }

  function moveBlock(blockId: string, dir: -1 | 1) {
    setDraft((d) => {
      if (!d) return d;
      const idx = d.blocks.findIndex((b) => b.id === blockId);
      if (idx < 0) return d;
      const target = idx + dir;
      if (target < 0 || target >= d.blocks.length) return d;
      return { ...d, blocks: arrayMove(d.blocks, idx, target) };
    });
  }

  function deleteBlock(blockId: string) {
    setDraft((d) => (d ? { ...d, blocks: d.blocks.filter((b) => b.id !== blockId) } : d));
    if (selectedId === blockId) setSelectedId(null);
  }

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);

    // Library → canvas (insert at end if dropped on canvas, after a
    // sortable item if dropped on one).
    if (activeId.startsWith(LIBRARY_PREFIX)) {
      const type = activeId.slice(LIBRARY_PREFIX.length) as MailBlock['type'];
      const newBlock = makeBlock(type);
      setDraft((d) => {
        if (!d) return d;
        if (overId === CANVAS_DROP_ID) {
          return { ...d, blocks: [...d.blocks, newBlock] };
        }
        const idx = d.blocks.findIndex((b) => b.id === overId);
        if (idx < 0) return { ...d, blocks: [...d.blocks, newBlock] };
        const next = [...d.blocks];
        next.splice(idx + 1, 0, newBlock);
        return { ...d, blocks: next };
      });
      setSelectedId(newBlock.id);
      return;
    }

    // Sortable reorder.
    if (activeId !== overId && overId !== CANVAS_DROP_ID) {
      setDraft((d) => {
        if (!d) return d;
        const from = d.blocks.findIndex((b) => b.id === activeId);
        const to = d.blocks.findIndex((b) => b.id === overId);
        if (from < 0 || to < 0) return d;
        return { ...d, blocks: arrayMove(d.blocks, from, to) };
      });
    }
  }

  async function save() {
    if (!draft) return;
    const d = draft;
    try {
      await upsert.mutateAsync({
        id: id ?? undefined,
        name: d.name,
        subject: d.subject,
        preheader: d.preheader,
        bgColor: d.bgColor,
        cardColor: d.cardColor,
        width: d.width,
        rounded: d.rounded,
        category: d.category,
        blocks: d.blocks,
        whatsappBody: d.whatsappBody ?? '',
        smsBody: d.smsBody ?? '',
      });
      toast({ title: 'Vorlage gespeichert' });
    } catch (err) {
      toast({
        title: 'Speichern fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  const selected = draft.blocks.find((b) => b.id === selectedId) ?? null;
  const blockIds = draft.blocks.map((b) => b.id);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to="/settings/mail-templates">
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
            Alle Vorlagen
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {dirty ? 'Ungespeicherte Änderungen' : 'Gespeichert'}
          </span>
          <Button size="sm" onClick={save} disabled={!dirty || upsert.isPending}>
            {upsert.isPending ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="mr-2 h-3.5 w-3.5" />
            )}
            Speichern
          </Button>
        </div>
      </div>

      {/* Channel tabs */}
      <ChannelTabs value={channel} onChange={setChannel} draft={draft} />

      {channel === 'email' ? (
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
            {/* Left column — meta + library + selected props */}
            <div className="space-y-3">
              <Card>
                <CardContent className="space-y-3 p-3">
                  <FieldText
                    label="Name (intern)"
                    value={draft.name}
                    onChange={(v) => patch('name', v)}
                  />
                  <FieldText
                    label="Betreff"
                    value={draft.subject}
                    onChange={(v) => patch('subject', v)}
                  />
                  <FieldText
                    label="Vorschau-Text (preheader)"
                    value={draft.preheader ?? ''}
                    onChange={(v) => patch('preheader', v)}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardContent className="space-y-3 p-3">
                  <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                    Container
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <ColorField
                      label="Hintergrund"
                      value={draft.bgColor}
                      onChange={(v) => patch('bgColor', v)}
                    />
                    <ColorField
                      label="Karte"
                      value={draft.cardColor}
                      onChange={(v) => patch('cardColor', v)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Breite: {draft.width} px</Label>
                    <Slider
                      value={[draft.width]}
                      min={400}
                      max={760}
                      step={20}
                      onValueChange={(v) => patch('width', v[0] ?? 600)}
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-md border border-border p-2">
                    <Label className="cursor-pointer text-sm">Abgerundete Ecken</Label>
                    <Switch
                      checked={draft.rounded}
                      onCheckedChange={(v) => patch('rounded', v)}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="space-y-2 p-3">
                  <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                    Blöcke ziehen
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Zieh einen Block in die Vorschau rechts. Reihenfolge dort per Drag,
                    Aktionen via Hover-Toolbar.
                  </p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {LIBRARY.map((b) => (
                      <LibraryItem key={b.type} type={b.type} label={b.label} icon={b.icon} />
                    ))}
                  </div>
                </CardContent>
              </Card>

              {selected && (
                <Card>
                  <CardContent className="space-y-3 p-3">
                    <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                      Eigenschaften — {selected.type}
                    </div>
                    <BlockProperties
                      block={selected}
                      onChange={(p) => patchBlock(selected.id, p)}
                    />
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right column — preview canvas + iframe export view */}
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground">
                  Hover über einen Block für ↑ ↓ ×. Greifer links zum Sortieren.
                </p>
                <div className="flex items-center gap-2">
                  <ToggleGroup
                    value={previewMode}
                    onChange={(v) => setPreviewMode(v as 'light' | 'dark')}
                    options={[
                      { v: 'light', label: 'Light', icon: <SunIcon className="h-3.5 w-3.5" /> },
                      { v: 'dark', label: 'Dark', icon: <MoonIcon className="h-3.5 w-3.5" /> },
                    ]}
                  />
                  <ToggleGroup
                    value={previewWidth}
                    onChange={(v) => setPreviewWidth(v as 'desktop' | 'mobile')}
                    options={[
                      {
                        v: 'desktop',
                        label: 'Desktop',
                        icon: <Monitor className="h-3.5 w-3.5" />,
                      },
                      {
                        v: 'mobile',
                        label: 'Mobil',
                        icon: <Smartphone className="h-3.5 w-3.5" />,
                      },
                    ]}
                  />
                </div>
              </div>
              <PreviewCanvas
                blockIds={blockIds}
                draft={draft}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onMove={moveBlock}
                onDeleteRequest={(id) => setConfirmDelete(id)}
                previewMode={previewMode}
                previewWidth={previewWidth}
              />
            </div>
          </div>
        </DndContext>
      ) : channel === 'whatsapp' ? (
        <WhatsAppEditor
          value={draft.whatsappBody ?? ''}
          onChange={(v) => patch('whatsappBody', v)}
        />
      ) : (
        <SmsEditor
          value={draft.smsBody ?? ''}
          onChange={(v) => patch('smsBody', v)}
        />
      )}

      {/* Delete confirm dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Block löschen?</DialogTitle>
            <DialogDescription>
              Dieser Schritt kann nicht rückgängig gemacht werden — der Block wird endgültig
              entfernt.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (confirmDelete) deleteBlock(confirmDelete);
                setConfirmDelete(null);
              }}
            >
              Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Channel tabs ────────────────────────────────────────────────────────

function ChannelTabs({
  value,
  onChange,
  draft,
}: {
  value: Channel;
  onChange: (c: Channel) => void;
  draft: MailTemplate;
}) {
  const items: ReadonlyArray<{ v: Channel; label: string; filled: boolean }> = [
    { v: 'email', label: 'E-Mail', filled: draft.blocks.length > 0 },
    { v: 'whatsapp', label: 'WhatsApp', filled: !!(draft.whatsappBody ?? '').trim() },
    { v: 'sms', label: 'SMS', filled: !!(draft.smsBody ?? '').trim() },
  ];
  return (
    <div className="flex flex-wrap items-center gap-1 rounded-md border border-border bg-card p-1">
      {items.map((it) => (
        <button
          key={it.v}
          type="button"
          onClick={() => onChange(it.v)}
          className={cn(
            'flex items-center gap-2 rounded px-3 py-1.5 text-sm font-medium transition-colors',
            value === it.v
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <span>{it.label}</span>
          <span
            className={cn(
              'inline-block h-1.5 w-1.5 rounded-full',
              it.filled
                ? value === it.v
                  ? 'bg-primary-foreground'
                  : 'bg-emerald-500'
                : 'bg-muted-foreground/30',
            )}
            title={it.filled ? 'Inhalt vorhanden' : 'Leer'}
          />
        </button>
      ))}
    </div>
  );
}

// ── Preview canvas (DnD-Kit sortable) ──────────────────────────────────

function PreviewCanvas({
  blockIds,
  draft,
  selectedId,
  onSelect,
  onMove,
  onDeleteRequest,
  previewMode,
  previewWidth,
}: {
  blockIds: string[];
  draft: MailTemplate;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onMove: (id: string, dir: -1 | 1) => void;
  onDeleteRequest: (id: string) => void;
  previewMode: 'light' | 'dark';
  previewWidth: 'desktop' | 'mobile';
}) {
  const { setNodeRef: setCanvasRef, isOver: isOverCanvas } = useDroppable({
    id: CANVAS_DROP_ID,
  });

  const isDark = previewMode === 'dark';
  const cardBg = isDark ? '#111827' : draft.cardColor;
  const textColor = isDark ? '#e5e7eb' : '#0b1220';
  const radius = draft.rounded ? 12 : 0;

  return (
    <div
      ref={setCanvasRef}
      className={cn(
        'rounded-md border p-4 transition-colors',
        isDark ? 'bg-[#0b1220] border-border' : 'bg-[#f4f6f9] border-border',
        isOverCanvas && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
      )}
      style={{ minHeight: 540 }}
    >
      <div
        className="mx-auto"
        style={{
          width: previewWidth === 'mobile' ? 380 : draft.width,
          maxWidth: '100%',
          background: cardBg,
          color: textColor,
          borderRadius: radius,
          overflow: 'hidden',
          border: '1px solid #e5e7eb',
        }}
      >
        {draft.blocks.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-muted-foreground">
            Zieh einen Block aus der Bibliothek hierher.
          </div>
        ) : (
          <SortableContext items={blockIds} strategy={verticalListSortingStrategy}>
            {draft.blocks.map((b, i) => (
              <SortableBlock
                key={b.id}
                block={b}
                isSelected={selectedId === b.id}
                isFirst={i === 0}
                isLast={i === draft.blocks.length - 1}
                onSelect={() => onSelect(b.id)}
                onMoveUp={() => onMove(b.id, -1)}
                onMoveDown={() => onMove(b.id, 1)}
                onDelete={() => onDeleteRequest(b.id)}
                isDark={isDark}
                draft={draft}
              />
            ))}
          </SortableContext>
        )}
      </div>
    </div>
  );
}

function SortableBlock({
  block,
  isSelected,
  isFirst,
  isLast,
  onSelect,
  onMoveUp,
  onMoveDown,
  onDelete,
  isDark,
  draft,
}: {
  block: MailBlock;
  isSelected: boolean;
  isFirst: boolean;
  isLast: boolean;
  onSelect: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  isDark: boolean;
  draft: MailTemplate;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group relative cursor-pointer outline-none',
        isSelected && 'ring-2 ring-primary ring-inset',
      )}
      onClick={onSelect}
    >
      <BlockPreview block={block} isDark={isDark} draft={draft} />

      {/* Hover toolbar overlay */}
      <div
        className={cn(
          'pointer-events-none absolute right-2 top-2 z-10 flex items-center gap-1 rounded-md border border-border bg-card/95 p-0.5 shadow-md backdrop-blur',
          'opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100',
          isSelected && 'pointer-events-auto opacity-100',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="flex h-6 w-6 cursor-grab items-center justify-center rounded text-muted-foreground hover:bg-secondary hover:text-foreground active:cursor-grabbing"
          title="Ziehen zum Sortieren"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onMoveUp}
          disabled={isFirst}
          className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-30"
          title="Nach oben"
        >
          ↑
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={isLast}
          className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-30"
          title="Nach unten"
        >
          ↓
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-destructive/15 hover:text-destructive"
          title="Löschen"
        >
          <XIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function BlockPreview({
  block,
  isDark,
  draft,
}: {
  block: MailBlock;
  isDark: boolean;
  draft: MailTemplate;
}) {
  const baseColor = isDark ? '#e5e7eb' : '#1f2937';
  const headingColor = isDark ? '#f3f4f6' : '#0b1220';
  const mutedColor = isDark ? '#9ca3af' : '#6b7280';

  switch (block.type) {
    case 'heading': {
      const sz = block.level === 1 ? 28 : block.level === 2 ? 22 : 18;
      return (
        <div style={{ padding: '18px 28px 6px 28px', textAlign: block.align }}>
          <span
            style={{
              fontSize: sz,
              fontWeight: 600,
              color: headingColor,
              fontFamily:
                "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
              lineHeight: 1.25,
            }}
          >
            {block.text}
          </span>
        </div>
      );
    }
    case 'paragraph':
      return (
        <div
          style={{
            padding: '6px 28px',
            color: baseColor,
            fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
            fontSize: 15,
            lineHeight: 1.6,
            textAlign: block.align,
            whiteSpace: 'pre-wrap',
          }}
        >
          {block.text}
        </div>
      );
    case 'button':
      return (
        <div style={{ padding: '14px 28px', textAlign: block.align }}>
          <span
            style={{
              display: 'inline-block',
              padding: '12px 22px',
              background: block.bgColor,
              color: block.color,
              borderRadius: 6,
              fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            {block.text || 'Button'}
          </span>
        </div>
      );
    case 'image':
      return (
        <div style={{ padding: '8px 28px', textAlign: 'center' }}>
          {block.url ? (
            <img
              src={block.url}
              alt={block.alt}
              style={{
                display: 'block',
                margin: '0 auto',
                maxWidth: '100%',
                width: Math.min(block.width, draft.width - 56),
                height: 'auto',
                borderRadius: 6,
              }}
            />
          ) : (
            <div
              style={{
                border: '1px dashed #d1d5db',
                padding: '24px',
                color: mutedColor,
                fontSize: 12,
              }}
            >
              Bild-URL fehlt
            </div>
          )}
        </div>
      );
    case 'hero':
      return (
        <div
          style={{
            padding: '36px 28px',
            background: block.bgColor,
            color: block.color,
            fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
          }}
        >
          <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.25 }}>{block.title}</div>
          {block.subtitle && (
            <div style={{ fontSize: 14, lineHeight: 1.5, opacity: 0.85, marginTop: 6 }}>
              {block.subtitle}
            </div>
          )}
        </div>
      );
    case 'divider':
      return (
        <div style={{ padding: '8px 28px' }}>
          <div style={{ height: 1, background: block.color }} />
        </div>
      );
    case 'spacer':
      return <div style={{ height: block.height }} aria-hidden />;
    case 'columns2':
      return (
        <div style={{ padding: '8px 28px', display: 'flex', gap: 16 }}>
          <div
            style={{
              flex: 1,
              fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
              fontSize: 14,
              lineHeight: 1.55,
              color: baseColor,
              whiteSpace: 'pre-wrap',
            }}
          >
            {block.left}
          </div>
          <div
            style={{
              flex: 1,
              fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
              fontSize: 14,
              lineHeight: 1.55,
              color: baseColor,
              whiteSpace: 'pre-wrap',
            }}
          >
            {block.right}
          </div>
        </div>
      );
  }
}

// ── Library item (drag-source) ─────────────────────────────────────────

function LibraryItem({
  type,
  label,
  icon,
}: {
  type: MailBlock['type'];
  label: string;
  icon: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${LIBRARY_PREFIX}${type}`,
  });
  return (
    <button
      ref={setNodeRef}
      type="button"
      {...attributes}
      {...listeners}
      className={cn(
        'flex cursor-grab items-center gap-2 rounded-md border border-border p-2 text-left text-xs hover:bg-secondary active:cursor-grabbing',
        isDragging && 'opacity-50',
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

// ── WhatsApp + SMS editors ──────────────────────────────────────────────

const WHATSAPP_FORMATS: ReadonlyArray<{
  label: string;
  icon: React.ReactNode;
  wrap: string;
  hint: string;
}> = [
  { label: 'Fett', icon: <Bold className="h-3.5 w-3.5" />, wrap: '*', hint: '*Text*' },
  { label: 'Kursiv', icon: <Italic className="h-3.5 w-3.5" />, wrap: '_', hint: '_Text_' },
];

function WhatsAppEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const ref = React.useRef<HTMLTextAreaElement>(null);

  function wrap(prefix: string) {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const sel = value.slice(start, end);
    const wrapped = `${prefix}${sel || 'Text'}${prefix}`;
    const next = value.slice(0, start) + wrapped + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + prefix.length, start + prefix.length + (sel || 'Text').length);
    });
  }

  function insertList() {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const insert = '\n• Listenpunkt\n• Listenpunkt\n';
    const next = value.slice(0, start) + insert + value.slice(start);
    onChange(next);
  }

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium">WhatsApp-Nachricht</div>
            <div className="text-[11px] text-muted-foreground">
              Plain Text mit WhatsApp-Markup. Merge-Tags wie &#123;&#123;ticketCode&#125;&#125; werden zur Sendezeit ersetzt.
            </div>
          </div>
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {value.length} / 4000
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {WHATSAPP_FORMATS.map((f) => (
            <Button
              key={f.label}
              type="button"
              size="sm"
              variant="outline"
              className="h-8"
              onClick={() => wrap(f.wrap)}
              title={f.hint}
            >
              {f.icon}
              <span className="ml-1.5">{f.label}</span>
            </Button>
          ))}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8"
            onClick={insertList}
          >
            <List className="mr-1.5 h-3.5 w-3.5" />
            Liste
          </Button>
        </div>
        <div className="grid gap-3 lg:grid-cols-2">
          <Textarea
            ref={ref}
            value={value}
            onChange={(e) => onChange(e.target.value.slice(0, 4000))}
            rows={14}
            placeholder={
              'Hallo {{callerName}},\n\n' +
              '*Ihr Anliegen* ist unter dem Code {{ticketCode}} bei uns angekommen.\n\n' +
              'Wir melden uns kurz zurück.'
            }
            className="font-mono text-sm"
          />
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm">
            <div className="mb-2 text-[10px] uppercase tracking-wider text-emerald-300">
              WhatsApp-Vorschau
            </div>
            <div
              className="rounded-lg bg-[#dcf8c6] px-3 py-2 text-[14px] leading-snug text-[#111]"
              dangerouslySetInnerHTML={{ __html: renderWhatsAppPreview(value) }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function renderWhatsAppPreview(s: string): string {
  // Mirror WhatsApp's tiny markdown subset — bold/italic/strike/mono.
  // Order matters: handle tildes longest-first so triple isn't consumed
  // by single. Also escape HTML before applying markup.
  const escaped = s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped
    .replace(/```([\s\S]+?)```/g, '<code style="font-family:monospace">$1</code>')
    .replace(/(^|\W)\*([^*\n]+)\*(?=\W|$)/g, '$1<strong>$2</strong>')
    .replace(/(^|\W)_([^_\n]+)_(?=\W|$)/g, '$1<em>$2</em>')
    .replace(/(^|\W)~([^~\n]+)~(?=\W|$)/g, '$1<s>$2</s>')
    .replace(/\n/g, '<br/>');
}

function SmsEditor({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const segments = value.length === 0 ? 0 : Math.ceil(value.length / 160);
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium">SMS</div>
            <div className="text-[11px] text-muted-foreground">
              Plain Text. Eine SMS = 160 Zeichen. Längere Nachrichten werden in mehrere SMS
              aufgeteilt — je nach Provider zählt jedes Segment einzeln.
            </div>
          </div>
          <span
            className={cn(
              'text-[11px] tabular-nums',
              segments > 4 ? 'text-amber-400' : 'text-muted-foreground',
            )}
          >
            {value.length} / {segments} {segments === 1 ? 'SMS' : 'SMS-Segmente'}
          </span>
        </div>
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value.slice(0, 1600))}
          rows={6}
          placeholder={
            'Hi {{callerName}}, Anliegen unter {{ticketCode}} ist eingegangen. ' +
            'Wir melden uns kurz zurueck. — {{fromName}}'
          }
        />
      </CardContent>
    </Card>
  );
}

// ── Block-property panel (unchanged) ────────────────────────────────────

function BlockProperties({
  block,
  onChange,
}: {
  block: MailBlock;
  onChange: (partial: Partial<MailBlock>) => void;
}) {
  switch (block.type) {
    case 'heading':
      return (
        <>
          <FieldText label="Text" value={block.text} onChange={(v) => onChange({ text: v })} />
          <FieldSelect
            label="Größe"
            value={String(block.level)}
            onChange={(v) => onChange({ level: Number(v) as 1 | 2 | 3 })}
            options={[
              { v: '1', label: 'H1 — sehr groß' },
              { v: '2', label: 'H2 — groß' },
              { v: '3', label: 'H3 — mittel' },
            ]}
          />
          <FieldAlign value={block.align} onChange={(v) => onChange({ align: v })} />
        </>
      );
    case 'paragraph':
      return (
        <>
          <FieldTextarea
            label="Text"
            value={block.text}
            onChange={(v) => onChange({ text: v })}
            rows={5}
          />
          <FieldAlign value={block.align} onChange={(v) => onChange({ align: v })} />
        </>
      );
    case 'button':
      return (
        <>
          <FieldText label="Beschriftung" value={block.text} onChange={(v) => onChange({ text: v })} />
          <FieldText label="Link-URL" value={block.url} onChange={(v) => onChange({ url: v })} />
          <div className="grid grid-cols-2 gap-2">
            <ColorField
              label="Hintergrund"
              value={block.bgColor}
              onChange={(v) => onChange({ bgColor: v })}
            />
            <ColorField label="Text" value={block.color} onChange={(v) => onChange({ color: v })} />
          </div>
          <FieldAlign value={block.align} onChange={(v) => onChange({ align: v })} />
        </>
      );
    case 'image':
      return (
        <>
          <FieldText label="Bild-URL" value={block.url} onChange={(v) => onChange({ url: v })} />
          <FieldText label="Alt-Text" value={block.alt} onChange={(v) => onChange({ alt: v })} />
        </>
      );
    case 'hero':
      return (
        <>
          <FieldText label="Titel" value={block.title} onChange={(v) => onChange({ title: v })} />
          <FieldText
            label="Untertitel"
            value={block.subtitle}
            onChange={(v) => onChange({ subtitle: v })}
          />
          <div className="grid grid-cols-2 gap-2">
            <ColorField
              label="Hintergrund"
              value={block.bgColor}
              onChange={(v) => onChange({ bgColor: v })}
            />
            <ColorField label="Text" value={block.color} onChange={(v) => onChange({ color: v })} />
          </div>
        </>
      );
    case 'divider':
      return (
        <ColorField label="Farbe" value={block.color} onChange={(v) => onChange({ color: v })} />
      );
    case 'spacer':
      return (
        <div className="space-y-1.5">
          <Label className="text-xs">Höhe: {block.height} px</Label>
          <Slider
            value={[block.height]}
            min={4}
            max={120}
            step={4}
            onValueChange={(v) => onChange({ height: v[0] ?? 24 })}
          />
        </div>
      );
    case 'columns2':
      return (
        <>
          <FieldTextarea
            label="Linke Spalte"
            value={block.left}
            onChange={(v) => onChange({ left: v })}
            rows={4}
          />
          <FieldTextarea
            label="Rechte Spalte"
            value={block.right}
            onChange={(v) => onChange({ right: v })}
            rows={4}
          />
        </>
      );
  }
}

// ── Tiny field helpers ──────────────────────────────────────────────────

function FieldText({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function FieldTextarea({
  label,
  value,
  onChange,
  rows,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows: number;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function FieldSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<{ v: string; label: string }>;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.v} value={o.v}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function FieldAlign({
  value,
  onChange,
}: {
  value: 'left' | 'center' | 'right';
  onChange: (v: 'left' | 'center' | 'right') => void;
}) {
  return (
    <FieldSelect
      label="Ausrichtung"
      value={value}
      onChange={(v) => onChange(v as 'left' | 'center' | 'right')}
      options={[
        { v: 'left', label: 'Links' },
        { v: 'center', label: 'Mitte' },
        { v: 'right', label: 'Rechts' },
      ]}
    />
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-1.5">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 w-10 cursor-pointer rounded-md border border-border bg-transparent"
        />
        <Input
          className="h-8 flex-1 font-mono text-xs"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={9}
        />
      </div>
    </div>
  );
}

function ToggleGroup<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: ReadonlyArray<{ v: T; label: string; icon?: React.ReactNode }>;
}) {
  return (
    <div className="inline-flex rounded-md border border-border bg-card p-0.5">
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          className={cn(
            'flex items-center gap-1 rounded px-2 py-1 text-xs',
            value === o.v
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.icon}
          {o.label}
        </button>
      ))}
    </div>
  );
}
